/**
 * ExportProjectModal - Modal for bulk exporting project documents to PDF
 * Supports full project export or per-book export for creative writing projects
 */

import { useState } from 'react';
import axios from 'axios';
import { authService } from '../services/auth-service';
import { logger } from '../utils/logger';
import type { ProjectInstance } from '../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Button,
  Label,
  Spinner,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';

interface ExportProjectModalProps {
  project: ProjectInstance;
  onClose: () => void;
  open?: boolean;
  /** Callback to open the generated PDF in the app's document viewer */
  onOpenPdfInViewer?: (pdfUrl: string, title: string) => void;
}

type ExportScope = 'full_project' | 'book';

interface BookOption {
  number: number;
  title: string;
}

export function ExportProjectModal({ 
  project, 
  onClose, 
  open = true,
  onOpenPdfInViewer 
}: ExportProjectModalProps) {
  const [scope, setScope] = useState<ExportScope>('full_project');
  const [selectedBook, setSelectedBook] = useState<number>(1);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{pdfUrl: string; documentCount: number} | null>(null);
  
  const isCreative = project.projectType === 'creative';
  const documentCount = project.documents?.length || 0;
  
  // Extract unique book numbers from documents
  interface DocWithBook {
    bookNumber?: number;
    bookTitle?: string;
    metadata?: { bookNumber?: number; bookTitle?: string };
  }
  
  const books: BookOption[] = [];
  const seenBooks = new Set<number>();
  
  for (const doc of (project.documents || []) as DocWithBook[]) {
    const bookNum = doc.bookNumber || doc.metadata?.bookNumber;
    if (bookNum && !seenBooks.has(bookNum)) {
      seenBooks.add(bookNum);
      books.push({
        number: bookNum,
        title: doc.bookTitle || doc.metadata?.bookTitle || `Book ${bookNum}`,
      });
    }
  }
  books.sort((a, b) => a.number - b.number);
  
  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    setProgress('Preparing export...');
    setResult(null);
    
    try {
      const tokens = await authService.getTokens();
      const baseURL = import.meta.env?.VITE_API_URL || '/api';
      
      // Use Lambda Function URL to bypass API Gateway's 29s timeout.
      // PDF generation for large projects can take 30-60s.
      const documentsFunctionUrl = import.meta.env?.VITE_DOCUMENTS_FUNCTION_URL
        ? String(import.meta.env.VITE_DOCUMENTS_FUNCTION_URL).trim()
        : '';
      
      setProgress('Loading documents...');
      
      const requestBody: { scope: ExportScope; bookNumber?: number; bookTitle?: string } = {
        scope,
      };
      
      if (scope === 'book') {
        requestBody.bookNumber = selectedBook;
        const book = books.find(b => b.number === selectedBook);
        if (book) {
          requestBody.bookTitle = book.title;
        }
      }
      
      setProgress('Generating PDF...');
      
      // Prefer Function URL (120s timeout) over API Gateway (29s timeout)
      // Function URL uses manual JWT verification which requires access token (not ID token)
      const useFunctionUrl = !!documentsFunctionUrl;
      const exportPath = `/v1/projects/${project.id}/export-pdf`;
      const exportUrl = useFunctionUrl
        ? `${documentsFunctionUrl.replace(/\/$/, '')}${exportPath}`
        : `${baseURL}${exportPath}`;
      
      // Function URL verifier expects access token; API Gateway Cognito authorizer accepts ID token
      const authToken = useFunctionUrl ? tokens?.accessToken : tokens?.idToken;
      
      const response = await axios.post(
        exportUrl,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 300000 // 5 minute timeout for large exports
        }
      );
      
      const data = response.data?.data || response.data;
      
      if (data && data.pdfUrl) {
        setResult({
          pdfUrl: data.pdfUrl,
          documentCount: data.documentCount || 0,
        });
        setProgress('Export complete!');
      } else {
        throw new Error('PDF generation failed - no download URL received');
      }
    } catch (err: unknown) {
      logger.error('Export error:', err);
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(axiosErr.response?.data?.error || axiosErr.message || 'Export failed');
      setProgress('');
    } finally {
      setIsExporting(false);
    }
  };
  
  const handleDownload = () => {
    if (!result) return;
    
    const title = scope === 'book' 
      ? `${project.seriesName || project.name}-Book-${selectedBook}.pdf`
      : `${project.seriesName || project.name}-Complete.pdf`;
    
    if (onOpenPdfInViewer) {
      onOpenPdfInViewer(result.pdfUrl, title);
      onClose();
    } else {
      window.open(result.pdfUrl, '_blank');
    }
  };
  
  const handleDownloadDirect = () => {
    if (!result) return;
    
    const filename = scope === 'book' 
      ? `${project.projectCode || 'Export'}-Book-${selectedBook}.pdf`
      : `${project.projectCode || 'Export'}-Complete.pdf`;
    
    const link = document.createElement('a');
    link.href = result.pdfUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export Project to PDF</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Project Info */}
          <div className="text-sm text-muted-foreground">
            <strong className="text-foreground">{project.name || project.projectCode}</strong>
            <br />
            {documentCount} document{documentCount !== 1 ? 's' : ''} available
            {isCreative && project.seriesName && (
              <><br />Series: {project.seriesName}</>
            )}
          </div>
          
          {/* Scope Selection */}
          <div className="space-y-2">
            <Label>Export Scope</Label>
            <RadixSelect
              value={scope}
              onValueChange={(value) => setScope(value as ExportScope)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_project">
                  Full Project (all documents)
                </SelectItem>
                {books.length > 0 && (
                  <SelectItem value="book">
                    Single Book
                  </SelectItem>
                )}
              </SelectContent>
            </RadixSelect>
          </div>
          
          {/* Book Selection (if scope is 'book') */}
          {scope === 'book' && books.length > 0 && (
            <div className="space-y-2">
              <Label>Select Book</Label>
              <RadixSelect
                value={String(selectedBook)}
                onValueChange={(value) => setSelectedBook(Number(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {books.map((book) => (
                    <SelectItem key={book.number} value={String(book.number)}>
                      Book {book.number}: {book.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </RadixSelect>
            </div>
          )}
          
          {/* Format Info */}
          <div className="p-3 bg-muted rounded-lg text-sm">
            <div className="font-medium mb-1">
              {isCreative ? 'Manuscript Format' : 'Aerospace Report Format'}
            </div>
            <div className="text-muted-foreground text-xs">
              {isCreative ? (
                <>
                  Courier 12pt, double-spaced, 1-inch margins, drop caps on chapter openings.
                  Documents grouped by type with bookmarks.
                </>
              ) : (
                <>
                  Professional engineering report format with headers, footers, 
                  table of contents, and PDF bookmarks.
                </>
              )}
            </div>
          </div>
          
          {/* Progress */}
          {isExporting && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner size="sm" />
              {progress}
            </div>
          )}
          
          {/* Error */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}
          
          {/* Result */}
          {result && (
            <div className="p-3 bg-success/10 border border-success/30 rounded-lg text-sm">
              <div className="font-medium text-success mb-2">
                Export Complete!
              </div>
              <div className="text-muted-foreground mb-3">
                Generated PDF with {result.documentCount} document{result.documentCount !== 1 ? 's' : ''}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleDownload} size="sm">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                  View PDF
                </Button>
                <Button onClick={handleDownloadDirect} variant="outline" size="sm">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                  </svg>
                  Download
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button 
              onClick={handleExport} 
              disabled={isExporting || documentCount === 0}
            >
              {isExporting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                  </svg>
                  Export PDF
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
